import React, { useState } from 'react';
import { type RescueCreateFosterHomeInput } from '../../lib/rescueQueries';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import FosterContactFields, {
  EMPTY_FOSTER_CONTACT,
  isDraftSubmittable,
  type FosterContactDraft,
} from '../../components/FosterContactFields/FosterContactFields';
import Button from '../../components/Button/Button';
import NoticeIcon from '../../components/NoticeIcon/NoticeIcon';
import useExistingFosterLookup from '../../hooks/useExistingFosterLookup';
import styles from './Devices.module.scss';

interface AddFosterModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: RescueCreateFosterHomeInput) => Promise<void>;
}

/**
 * Saves a foster home ahead of any placement.
 *
 * Email and phone are required here, unlike the nullable stored snapshot — `RescueCreateFosterHomeInput`
 * demands both, since they're what the reuse matcher keys on.
 *
 * The mutation is reuse-first, so rather than let a duplicate silently become an update, this checks
 * `rescueFosterHomeForContact` on blur and says so.
 */
export default function AddFosterModal({ open, onClose, onCreate }: AddFosterModalProps) {
  const [contact, setContact] = useState<FosterContactDraft>(EMPTY_FOSTER_CONTACT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { existingName, check: checkForExisting, clear: clearExisting } = useExistingFosterLookup(contact);

  // they/them deliberately: a first name doesn't tell us the volunteer's pronouns.
  const noticeSubject = contact.firstName.trim() || 'this foster';
  const canSubmit = isDraftSubmittable(contact) && !submitting;

  function reset() {
    setContact(EMPTY_FOSTER_CONTACT);
    setError(null);
    clearExisting();
  }

  function handleClose() {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  }

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({
        contactFirstName: contact.firstName.trim(),
        contactLastName: contact.lastName.trim() || null,
        contactEmail: contact.email.trim(),
        contactPhoneNumber: contact.phone.trim(),
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this foster.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add a foster</ModalTitle>
      <ModalSubtitle>
        Add someone you work with to your roster now, and assign them a device whenever you&apos;re ready.
      </ModalSubtitle>

      <FosterContactFields
        value={contact}
        onChange={setContact}
        onContactBlur={checkForExisting}
        disabled={submitting}
      />

      <div className={`${styles.notice} ${styles.noticeInfo}`}>
        <NoticeIcon variant="info" />
        <div>
          Nothing is sent to {noticeSubject} now. Their foster household is created the first time you assign them a
          device — that&apos;s when they&apos;ll set up their Safe Zone and WiFi.
        </div>
      </div>

      {existingName && (
        <div className={`${styles.notice} ${styles.noticeCaution}`}>
          <NoticeIcon variant="caution" />
          <div>
            <strong>{existingName}</strong> is already on your roster. Saving will update that home&apos;s contact
            details instead of adding a second one.
          </div>
        </div>
      )}

      {error && (
        <p className={styles.modalError} role="alert">
          {error}
        </p>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Saving…' : existingName ? 'Update Roster' : 'Add to Roster'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
